import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { DashStepperNavigation } from './DashStepperNavigation';

/** Gap between two tiles, in px. Part of the offset the track is translated by. */
const SPACING = 10;
const AUTOPLAY_INTERVAL = 3000;

const CarouselHost = styled.div`
  /**
   * The component measures the viewport element to compute tile widths, so the host must not
   * be inline — any width a consumer sets would then only be honoured when the host happens to
   * be blockified by a flex/grid parent. A column also lets the gap below give way (see Gap).
   */
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  margin-block-start: 1rem;
  padding-block: 0.5rem;
`;

const CarouselViewport = styled.section`
  /** Never the part that gives: the slide copy is the content, the gap below it is the slack. */
  flex: none;
  overflow: hidden;
`;

const CarouselTrack = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: space-between;
  transition: transform 350ms ease-in;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Slide = styled.li`
  /** Shrink-to-fit is not enough: the inline width is what makes the track measurable. */
  flex: 0 0 auto;
`;

/**
 * A shrinkable spacer rather than a margin on the stepper, because a margin cannot give way.
 * When a consumer squeezes the host this gap is what collapses, so the dashes stay whole and
 * whatever sits above the carousel keeps its place.
 */
const Gap = styled.div`
  flex: 0 1 clamp(1rem, 6dvh, 3rem);
`;

const StepperNavigation = styled(DashStepperNavigation)`
  flex: none;
`;

interface CarouselProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Auto-advancing single-slide carousel with a dash stepper beneath it.
 *
 * The track is one flex row as wide as all slides together, translated by whole tiles —
 * the `transition: transform` on the track is what animates the move.
 */
export const Carousel = ({ children, className }: CarouselProps) => {
  const slides = React.Children.toArray(children);
  const slideCount = slides.length;

  const viewportRef = useRef<HTMLElement>(null);
  const [tileWidth, setTileWidth] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Measured rather than assumed: the host width is a `clamp()` of viewport units, so the
  // tile width only exists after layout and changes on every resize.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const observer = new ResizeObserver(([entry]) => {
      setTileWidth(entry.contentRect.width);
    });
    observer.observe(viewport);

    return () => observer.disconnect();
  }, []);

  const goToNext = useCallback(() => {
    setCurrentSlide((slide) => (slide + 1) % slideCount);
  }, [slideCount]);

  // `currentSlide` is in the deps so a manual jump restarts the countdown rather than
  // letting the slide the user picked be cut short by a timer already half spent.
  useEffect(() => {
    const interval = setInterval(goToNext, AUTOPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [goToNext, currentSlide]);

  const trackWidth = slideCount * tileWidth + (slideCount - 1) * SPACING;
  const offset = currentSlide * (tileWidth + SPACING);

  return (
    <CarouselHost className={className} data-test-id="carousel">
      <CarouselViewport ref={viewportRef}>
        <CarouselTrack
          style={{
            width: `${trackWidth}px`,
            transform: `translateX(-${offset}px)`,
          }}
        >
          {slides.map((slide, index) => (
            <Slide key={index} style={{ width: `${tileWidth}px` }}>
              {slide}
            </Slide>
          ))}
        </CarouselTrack>
      </CarouselViewport>

      <Gap />

      <StepperNavigation
        currentStep={currentSlide}
        stepCount={slideCount}
        onStepChange={setCurrentSlide}
      />
    </CarouselHost>
  );
};
